import React, {useContext, useEffect, useRef, useState} from 'react'
import {useNavigate, useParams, useBlocker} from 'react-router-dom'
import AppContext, {AppContextData} from '../../appContext'
import LimeController from '../../controllers/LimeController'
import LimeProgramResponse from '../../controllers/LimeProgramResponse'
import LimeProgramLabInputs from '../../controllers/LimeProgramLabInputs'
import LimeProgramProductSources from '../../controllers/LimeProgramProductSources'
import LimePlayFieldRow from '../../controllers/LimePlayFieldRow'
import LimeTemplateBasicResult from '../../controllers/LimeTemplateBasicResult'
import LimeProductResponse from '../../controllers/LimeProductResponse'
import LimeProductType from '../../controllers/LimeProductType'
import LabInputUnit from '../../controllers/LabInputUnit'
import {dateFormat} from '../../date'
import Dialog from '../../components/Dialog'
import ProgramApprove from '../program/ProgramApprove'
import UserController from '../../controllers/UserController'
import {showSuccessOrFailed} from '../../Snacks'
import Failed from '../../components/Failed'
import {calculateLandRows, buildProductValues, CalcOptions, extractCalculatedOutputs} from './limeCalcs'
import {matchAllRows, toRowsWithId, rowsToSamplePoints, isMeq100g} from './limeSoilUtils'
import SampleLocationsDialog from './SampleLocationsDialog'
import LimePlayfieldUnified, {ProductNames} from './LimePlayfieldUnified'
import LimeBlocksTab from './LimeBlocksTab'
import LimeOutputTab from './LimeOutputTab'
import ApplicationDataDialog from './ApplicationDataDialog'
import {useBoundaries} from '../../controllers/useBoundaries'
import {boundariesToRings} from '../../controllers/boundaryGeo'
import LandResponse from '../../controllers/LandResponse'
import TemplateData from '../../controllers/TemplateData'
import SelectNumber from '../../components/SelectNumber'
import SelectNumberNullable from '../../components/SelectNumberNullable'
import ProductCharacteristics from './ProductCharacteristics'
import Input from '../../components/Input'
import Number from '../../components/Number'
import {ChevronLeftIcon, ChevronRightIcon} from '@heroicons/react/24/outline'
import {switcher} from '../../wrapper'

type ActiveTab = 'master' | 'blocks' | 'playfield' | 'output'

interface LimeProgramProps {
    id?: number
    footer?: React.ReactNode
}

function getProductNames(products: LimeProductResponse[], productSources: LimeProgramProductSources | null, context: AppContextData): ProductNames {
    const findProduct = (id: number | null | undefined, fallbackKey: string) => {
        if (!id) return context.word(fallbackKey);
        return products.find(p => p.id === id)?.name ?? context.word(fallbackKey);    
    }
    
    return {
        calciticLime: findProduct(productSources?.calciticLimeProductId, 'calcitic_lime'),
        dolomiticLime: findProduct(productSources?.dolomiticLimeProductId, 'dolomitic_lime'),
        magnesium: findProduct(productSources?.magnesiumProductId, 'magnesium'),
        gypsum: findProduct(productSources?.gypsumProductId, 'gypsum'),
        potassium: findProduct(productSources?.potassiumProductId, 'potassium'),
        phosphate: findProduct(productSources?.phosphateProductId, 'phosphate')
    };
}

const LimeProgram: React.FC<LimeProgramProps> = (props) => {
    const context = useContext(AppContext)
    const navigate = useNavigate()
    const params = useParams()
    const id = props.id ?? parseInt(params.id as string)

    const [program, setProgram] = useState<LimeProgramResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [showApprover, setShowApprover] = useState(false)
    const [warningPopup, setWarningPopup] = useState(false)

    // Editable state
    const [name, setName] = useState('')
    const [regionId, setRegionId] = useState<number | null>(null)
    const [regionName, setRegionName] = useState('')
    const [labInputUnit, setLabInputUnit] = useState<LabInputUnit>(LabInputUnit.MgKgPpm)
    const [productSources, setProductSources] = useState<LimeProgramProductSources | null>(null)
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
    const [templateData, setTemplateData] = useState<TemplateData | null>(null)
    const [templates, setTemplates] = useState<LimeTemplateBasicResult[]>([])
    const [products, setProducts] = useState<LimeProductResponse[]>([])
    const [labInputs, setLabInputs] = useState<LimeProgramLabInputs>({rows: []})
    const [calculatedRows, setCalculatedRows] = useState<LimePlayFieldRow[]>([])
    const [labInputsChanged, setLabInputsChanged] = useState(false)
    const [tab, setTab] = useState<ActiveTab>('master')
    const [showApplicationData, setShowApplicationData] = useState(false)
    const [showSampleLocations, setShowSampleLocations] = useState(false)
    const [selectedLandIds, setSelectedLandIds] = useState<number[]>([])
    const [showUnmatched, setShowUnmatched] = useState(true)
    const [farmLands, setFarmLands] = useState<LandResponse[]>([])

    // Fetch boundaries for all farm blocks (so Block tab can show areas for all)
    const allLandIds = React.useMemo(() => farmLands.map(l => l.id), [farmLands])
    const {geometryMap, loading: boundaryLoading, error: boundaryError} = useBoundaries(allLandIds)

    // Rings of the selected blocks for map components (one ring per part of a multipart boundary)
    const multipleBoundaries = React.useMemo(() => {
        const geoms = selectedLandIds
            .map(id => geometryMap[id])
            .filter((g): g is NonNullable<typeof g> => !!g)
        return boundariesToRings(geoms)
    }, [geometryMap, selectedLandIds])

    // Filter lab inputs to selected blocks + optionally unmatched.
    // indexMap maps filtered index → original labInputs index.
    const {filteredLabInputs, filteredCalculatedRows, indexMap} = React.useMemo(() => {
        const rows: typeof labInputs.rows = []
        const calcs: LimePlayFieldRow[] = []
        const map: number[] = []
        labInputs.rows.forEach((row, idx) => {
            const include = row.landId === null ? showUnmatched : selectedLandIds.includes(row.landId)
            if (!include) return
            rows.push(row)
            const calc = calculatedRows[idx]
            if (calc) calcs.push(calc)
            map.push(idx)
        })
        return {filteredLabInputs: {rows}, filteredCalculatedRows: calcs, indexMap: map}
    }, [labInputs, calculatedRows, selectedLandIds, showUnmatched])

    // Compute live sample counts per block from labInputs
    const {sampleCounts, unmatchedCount} = React.useMemo(() => {
        const counts = new Map<number, number>()
        let unmatched = 0
        for (const row of labInputs.rows) {
            if (row.landId != null) {
                counts.set(row.landId, (counts.get(row.landId) ?? 0) + 1)
            } else {
                unmatched++
            }
        }
        return {sampleCounts: counts, unmatchedCount: unmatched}
    }, [labInputs])

    // Sample location data for the blocks tab map
    const sampleRows = React.useMemo(() => toRowsWithId(filteredLabInputs), [filteredLabInputs])
    const samplePoints = React.useMemo(() => rowsToSamplePoints(sampleRows), [sampleRows])
    const meq100gMode = isMeq100g(labInputUnit)

    // Track original values
    const originalRef = useRef<{
        name: string
        productSources: LimeProgramProductSources
        templateId: number
        labInputs: LimeProgramLabInputs
    } | null>(null)

    useEffect(() => {
        if (isNaN(id)) {
            navigate('/liming/programs')
            return
        }

        // First load the program to get its region, then load products by region
        LimeController.getProgram(id).then(programData => {
            setProgram(programData)
            setName(programData.name)
            setRegionId(programData.regionId)
            setRegionName(programData.regionName)
            setLabInputUnit(programData.labInputUnit)
            setProductSources(programData.productSources)
            setSelectedTemplateId(programData.limeTemplateId)
            setLabInputs(programData.labInputs)
            setSelectedLandIds(programData.selectedLandIds)

            // Now load templates, products, and farm lands
            return Promise.all([
                LimeController.getApprovedTemplates(),
                LimeController.getProductsByRegion(programData.regionId),
                LimeController.getFarmLands(programData.farmId)
            ]).then(([templatesData, productsData, farmLandsData]) => {
                setFarmLands(farmLandsData)
                setTemplates(templatesData)
                setProducts(productsData)

                // Set template data and calculate rows (FIX 3+15: pass product data and depths)
                if (programData.templateData) {
                    setTemplateData(programData.templateData)
                    const ps = programData.productSources
                    const findProd = (pid: number | null) => pid ? productsData.find(p => p.id === pid) : undefined
                    const opts: CalcOptions = {
                        limeGypsumDepthCm: ps.limeGypsumDepthCm,
                        pkDepthCm: ps.pkDepthCm,
                        productSelected: {
                            calcitic: ps.calciticLimeProductId != null,
                            dolomitic: ps.dolomiticLimeProductId != null,
                            magnesium: ps.magnesiumProductId != null,
                            gypsum: ps.gypsumProductId != null,
                            potassium: ps.potassiumProductId != null,
                            phosphate: ps.phosphateProductId != null,
                        },
                        products: buildProductValues(
                            findProd(ps.calciticLimeProductId),
                            findProd(ps.dolomiticLimeProductId),
                            findProd(ps.magnesiumProductId),
                            findProd(ps.gypsumProductId),
                            findProd(ps.potassiumProductId),
                            findProd(ps.phosphateProductId)
                        )
                    }
                    setCalculatedRows(calculateLandRows(programData.labInputs, programData.templateData, opts))
                }

                // Store original for change detection
                originalRef.current = {
                    name: programData.name,
                    productSources: programData.productSources,
                    templateId: programData.limeTemplateId,
                    labInputs: programData.labInputs
                }

                setLoading(false)
            })
        }).catch(() => {
            context.showSnack(<Failed title={context.word('failed_to_load')}/>)
            navigate('/liming/programs')
        })
    }, [id])

    // Fetch new template data when user switches the template
    useEffect(() => {
        if (!selectedTemplateId || !program) return
        if (selectedTemplateId === program.limeTemplateId) {
            // Same template as loaded — use the already-fetched data
            setTemplateData(program.templateData)
            return
        }
        LimeController.get(selectedTemplateId).then(template => {
            setTemplateData(template.data)
        }).catch(() => {
            context.showSnack(<Failed title={context.word('failed_to_load')}/>)
        })
    }, [selectedTemplateId])

    // Recalculate when lab inputs, template data, products, or depths change
    useEffect(() => {
        if (templateData) {
            setCalculatedRows(calculateLandRows(labInputs, templateData, buildCalcOptions()))
        }
    }, [labInputs, templateData, productSources, products])

    // Detect unsaved changes
    const hasChanges = (): boolean => {
        if (!originalRef.current || !program) return false

        if (name !== originalRef.current.name) return true
        if (selectedTemplateId !== originalRef.current.templateId) return true
        if (JSON.stringify(productSources) !== JSON.stringify(originalRef.current.productSources)) return true
        if (labInputsChanged) return true

        return false
    }

    // Block navigation when there are unsaved changes
    const blocker = useBlocker(() => hasChanges())

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (hasChanges()) {
                event.preventDefault()
            }
        }
        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [name, productSources, selectedTemplateId, labInputs, labInputsChanged])

    function saveProgram(): Promise<void> {
        if (!program || !productSources || !selectedTemplateId || !regionId) {
            return Promise.reject()
        }

        // Merge calculated output values into lab input rows before saving
        // This stores the frontend-calculated values so Excel export can read them directly
        const labInputsWithCalcs: LimeProgramLabInputs = {
            rows: labInputs.rows.map((row, idx) => {
                const calc = calculatedRows[idx]
                if (!calc) return row
                return { ...row, outputs: extractCalculatedOutputs(calc) }
            })
        }

        return showSuccessOrFailed(
            context,
            LimeController.updateProgram({
                id: program.id,
                name,
                selectedLandIds,
                regionId,
                limeTemplateId: selectedTemplateId,
                productSources,
                labInputs: labInputsWithCalcs
            }),
            'successfully_saved'
        ).then(() => {
            // Update original ref after save
            originalRef.current = {
                name,
                productSources,
                templateId: selectedTemplateId,
                labInputs
            }
            setLabInputsChanged(false)

            // Reset approval status after saving (must resubmit)
            if (program) {
                setProgram({
                    ...program,
                    selectedLandIds,
                    submittedOn: null,
                    approvedOn: null,
                    approvedBy: null
                })
            }
        })
    }

    function submitProgram(approverId: number) {
        showSuccessOrFailed(context, LimeController.submitProgram({id, approverId}))
            .then(() => {
                setShowApprover(false)
                if (program) {
                    setProgram({
                        ...program,
                        submittedOn: new Date(),
                        assignedApproverId: approverId
                    })
                }
            })
    }

    function finishProgram() {
        const doFinish = () => {
            showSuccessOrFailed(context, LimeController.finishProgram(id), context.word('auto_approved'))
                .then(() => {
                    if (program) {
                        const now = new Date()
                        setProgram({
                            ...program,
                            submittedOn: now,
                            approvedOn: now,
                            autoApproved: true
                        })
                    }
                })
        }

        if (somethingChanged) {
            saveProgram().then(doFinish)
        } else {
            doFinish()
        }
    }

    // Build CalcOptions from current product selections and depths (FIX 3 + FIX 15)
    function buildCalcOptions(): CalcOptions {
        const findProduct = (id: number | null | undefined) =>
            id ? products.find(p => p.id === id) : undefined

        return {
            limeGypsumDepthCm: productSources?.limeGypsumDepthCm,
            pkDepthCm: productSources?.pkDepthCm,
            productSelected: {
                calcitic: productSources?.calciticLimeProductId != null,
                dolomitic: productSources?.dolomiticLimeProductId != null,
                magnesium: productSources?.magnesiumProductId != null,
                gypsum: productSources?.gypsumProductId != null,
                potassium: productSources?.potassiumProductId != null,
                phosphate: productSources?.phosphateProductId != null,
            },
            products: buildProductValues(
                findProduct(productSources?.calciticLimeProductId),
                findProduct(productSources?.dolomiticLimeProductId),
                findProduct(productSources?.magnesiumProductId),
                findProduct(productSources?.gypsumProductId),
                findProduct(productSources?.potassiumProductId),
                findProduct(productSources?.phosphateProductId)
            )
        }
    }

    // Build land name lookup (id -> name) for display
    const landNames = React.useMemo(() => {
        const map = new Map<number, string>()
        for (const land of farmLands) {
            if (land.name) map.set(land.id, land.name)
        }
        return map
    }, [farmLands])

    // Always run spatial matching when data changes (sets landId + landName)
    function updateLabInputs(newLabInputs: LimeProgramLabInputs) {
        const matched = matchAllRows(newLabInputs, geometryMap, landNames)
        setLabInputs(matched)
        setLabInputsChanged(true)
    }

    // Re-run matching when boundaries arrive after lab inputs were already set
    const boundaryKeys = Object.keys(geometryMap).join(',')
    React.useEffect(() => {
        if (labInputs.rows.length === 0 || !boundaryKeys) return
        const rematched = matchAllRows(labInputs, geometryMap, landNames)
        const changed = rematched.rows.some((r, i) => r.landId !== labInputs.rows[i]?.landId)
        if (changed) setLabInputs(rematched)
    }, [boundaryKeys])

    // Update a single override value (filtered index → original index via indexMap)
    function updateRowOverride(filteredIndex: number, field: 'limeTypeOverride' | 'limeNeedOverride' | 'magnesiumNeedOverride' | 'gypsumNeedOverride' | 'kNeedOverride' | 'pNeedOverride' | 'elementalSulphurOverride', value: string | number | null) {
        const originalIndex = indexMap[filteredIndex]
        if (originalIndex === undefined) return
        const newRows = [...labInputs.rows]
        const row = newRows[originalIndex]
        if (!row) return
        newRows[originalIndex] = {...row, [field]: value}
        setLabInputs({rows: newRows})
        setLabInputsChanged(true)
    }

    // Update override value for multiple rows at once
    function updateMultipleRowOverrides(filteredIndices: number[], field: 'limeTypeOverride' | 'limeNeedOverride' | 'magnesiumNeedOverride' | 'gypsumNeedOverride' | 'kNeedOverride' | 'pNeedOverride' | 'elementalSulphurOverride', value: string | number | null) {
        const newRows = [...labInputs.rows]
        filteredIndices.forEach(fi => {
            const originalIndex = indexMap[fi]
            if (originalIndex === undefined) return
            const row = newRows[originalIndex]
            if (row) {
                newRows[originalIndex] = {...row, [field]: value}
            }
        })
        setLabInputs({rows: newRows})
        setLabInputsChanged(true)
    }

    if (loading) {
        return <div className="p-4">{context.word('loading')}...</div>
    }

    if (!program) {
        return <div className="p-4">{context.word('program_not_found')}</div>
    }

    const isSubmitted = program.submittedOn !== null
    const isApproved = program.approvedOn !== null
    const somethingChanged = hasChanges()
    const hasOverrides = labInputs.rows.some(r =>
        r.limeTypeOverride !== null || r.limeNeedOverride !== null ||
        r.magnesiumNeedOverride !== null ||
        r.gypsumNeedOverride !== null || r.kNeedOverride !== null ||
        r.pNeedOverride !== null || r.elementalSulphurOverride !== null
    )
    const canAutoApprove = program.templateApproved && !hasOverrides

    return (
        <div className="flex flex-col h-screen overflow-hidden">
            {/* Dialogs */}
            <Dialog
                title={context.word('select_approver')}
                body={<ProgramApprove onCancel={() => setShowApprover(false)} onSubmit={submitProgram} fetchApprovers={UserController.limingApprovers}/>}
                show={showApprover}
                setShow={setShowApprover}
            />

            <Dialog
                title={context.word('warning')}
                show={warningPopup}
                setShow={setWarningPopup}
                body={
                    <div className="p-2 text-xl">
                        <div>{context.word('must_resubmit_program_for_approval')}. {context.word('would_you_like_to_continue')}?</div>
                        <div className="flex m-2 text-white justify-center">
                            <button
                                className="bg-primary p-2 rounded m-1 px-4"
                                onClick={() => {
                                    saveProgram()
                                    setWarningPopup(false)
                                }}
                            >
                                {context.word('yes')}
                            </button>
                            <button
                                className="bg-error-500 p-2 rounded m-1 px-4"
                                onClick={() => setWarningPopup(false)}
                            >
                                {context.word('no')}
                            </button>
                        </div>
                    </div>
                }
            />

            <Dialog
                title={context.word('save_changes')}
                show={blocker.state === 'blocked'}
                setShow={() => blocker.reset?.()}
                body={
                    <div className="p-4">
                        <div className="text-center mb-4">
                            <div>{context.word('unsaved_changes_lost')}.</div>
                            <div>{context.word('save_before_closing')}?</div>
                        </div>
                        <div className="flex w-full justify-between items-center">
                            <div
                                className="uppercase text-error-400 pr-4 cursor-pointer underline"
                                onClick={() => blocker.proceed?.()}
                            >
                                {context.word('discard_changes')}
                            </div>
                            <div
                                className="btn bg-primary"
                                onClick={() => {
                                    saveProgram().then(() => {
                                        blocker.proceed?.()
                                    })
                                }}
                            >
                                {context.word('save')}
                            </div>
                        </div>
                    </div>
                }
            />

            {/* Tab Navigation - same styling as fertiliser program */}
            <div className="sticky top-0 z-10">
                <div className="bg-gray-100 flex overflow-x-scroll pt-1 text-sm items-center overflow-hidden">
                    <div className="px-4"></div>
                    <div className="cursor-pointer">
                        <ChevronLeftIcon className="text-gray-500 h-4" onClick={() => {
                            const tabs: ActiveTab[] = ['master', 'blocks', 'playfield', 'output']
                            const idx = tabs.indexOf(tab)
                            setTab(tabs[(idx - 1 + tabs.length) % tabs.length] ?? 'master')
                        }}/>
                    </div>
                    <div className="cursor-pointer">
                        <ChevronRightIcon className="text-gray-500 h-4" onClick={() => {
                            const tabs: ActiveTab[] = ['master', 'blocks', 'playfield', 'output']
                            const idx = tabs.indexOf(tab)
                            setTab(tabs[(idx + 1) % tabs.length] ?? 'master')
                        }}/>
                    </div>
                    <div className="w-4"></div>

                    {/* Master Tab */}
                    {(['master', 'blocks', 'playfield', 'output'] as ActiveTab[]).map(t => {
                        const tabClass = tab === t
                            ? 'flex bg-white px-2 py-1 border-l border-r border-r-4 border-gray-300 rounded-t border-l-gray-300 border-t-2 border-t-primary-600 cursor-pointer'
                            : 'flex px-2 py-1 border-r-4 border-gray-100 cursor-pointer'
                        const label = t === 'master' ? context.word('master')
                            : t === 'blocks' ? context.word('blocks')
                            : t === 'output' ? context.word('output')
                            : context.word('playfields')
                        const changed = t === 'master' ? somethingChanged
                            : t === 'playfield' ? labInputsChanged
                            : false
                        return (
                            <div key={t} className={tabClass} onClick={() => setTab(t)}>
                                <span className="text-red-500">{changed ? '*' : ' '}</span>
                                <span className="px-2 align-middle text-black whitespace-nowrap">{label}</span>
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* Tab Content */}
            {switcher(tab, c => c
                .case('master', () => (
                    <div className="flex-1 overflow-auto">
                        <div className="flex w-full justify-center">
                            <div className="pt-4">
                                <div className="text-sm text-gray-600">{dateFormat(new Date())}</div>
                                <div className="font-bold uppercase text-primary-800 tracking-wider text-xl leading-none">
                                    {program.clientName}
                                </div>

                                <table>
                                    <tbody>
                                        <tr>
                                            <td className="p-1">{context.word('program_name')}:</td>
                                            <td className="p-1">
                                                <Input
                                                    value={name}
                                                    change={setName}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('farm')}:</td>
                                            <td className="p-1">
                                                <span className="px-2">{program.farmName}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('region')}:</td>
                                            <td className="p-1">
                                                <span className="px-2">{regionName}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('template')}:</td>
                                            <td className="p-1">
                                                <SelectNumber
                                                    options={templates}
                                                    textFunc={t => t.name}
                                                    valueFunc={t => t.id}
                                                    value={selectedTemplateId || 0}
                                                    onChange={v => setSelectedTemplateId(v)}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('calcitic_lime_product')}:</td>
                                            <td className="p-1">
                                                <div className="flex items-center">
                                                    <SelectNumberNullable
                                                        emptyText={context.word('none')}
                                                        options={products.filter(p => p.type === LimeProductType.Calcite && p.active)}
                                                        textFunc={p => p.name}
                                                        valueFunc={p => p.id}
                                                        value={productSources?.calciticLimeProductId ?? null}
                                                        onChange={v => productSources && setProductSources({...productSources, calciticLimeProductId: v})}
                                                    />
                                                    <ProductCharacteristics product={products.find(p => p.id === productSources?.calciticLimeProductId)}/>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('dolomitic_lime_product')}:</td>
                                            <td className="p-1">
                                                <div className="flex items-center">
                                                    <SelectNumberNullable
                                                        emptyText={context.word('none')}
                                                        options={products.filter(p => p.type === LimeProductType.Dolomite && p.active)}
                                                        textFunc={p => p.name}
                                                        valueFunc={p => p.id}
                                                        value={productSources?.dolomiticLimeProductId ?? null}
                                                        onChange={v => productSources && setProductSources({...productSources, dolomiticLimeProductId: v})}
                                                    />
                                                    <ProductCharacteristics product={products.find(p => p.id === productSources?.dolomiticLimeProductId)}/>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('magnesium_product')}:</td>
                                            <td className="p-1">
                                                <div className="flex items-center">
                                                    <SelectNumberNullable
                                                        emptyText={context.word('none')}
                                                        options={products.filter(p => p.type === LimeProductType.Magnesium && p.active)}
                                                        textFunc={p => p.name}
                                                        valueFunc={p => p.id}
                                                        value={productSources?.magnesiumProductId ?? null}
                                                        onChange={v => productSources && setProductSources({...productSources, magnesiumProductId: v})}
                                                    />
                                                    <ProductCharacteristics product={products.find(p => p.id === productSources?.magnesiumProductId)}/>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('gypsum_product')}:</td>
                                            <td className="p-1">
                                                <div className="flex items-center">
                                                    <SelectNumberNullable
                                                        emptyText={context.word('none')}
                                                        options={products.filter(p => p.type === LimeProductType.Gypsum && p.active)}
                                                        textFunc={p => p.name}
                                                        valueFunc={p => p.id}
                                                        value={productSources?.gypsumProductId ?? null}
                                                        onChange={v => productSources && setProductSources({...productSources, gypsumProductId: v})}
                                                    />
                                                    <ProductCharacteristics product={products.find(p => p.id === productSources?.gypsumProductId)}/>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('potassium_source')}:</td>
                                            <td className="p-1">
                                                <div className="flex items-center">
                                                    <SelectNumberNullable
                                                        emptyText={context.word('none')}
                                                        options={products.filter(p => p.type === LimeProductType.Potassium && p.active)}
                                                        textFunc={p => p.name}
                                                        valueFunc={p => p.id}
                                                        value={productSources?.potassiumProductId ?? null}
                                                        onChange={v => productSources && setProductSources({...productSources, potassiumProductId: v})}
                                                    />
                                                    <ProductCharacteristics product={products.find(p => p.id === productSources?.potassiumProductId)}/>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('phosphate_source')}:</td>
                                            <td className="p-1">
                                                <div className="flex items-center">
                                                    <SelectNumberNullable
                                                        emptyText={context.word('none')}
                                                        options={products.filter(p => p.type === LimeProductType.Phosphate && p.active)}
                                                        textFunc={p => p.name}
                                                        valueFunc={p => p.id}
                                                        value={productSources?.phosphateProductId ?? null}
                                                        onChange={v => productSources && setProductSources({...productSources, phosphateProductId: v})}
                                                    />
                                                    <ProductCharacteristics product={products.find(p => p.id === productSources?.phosphateProductId)}/>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('lime_gypsum_depth')} (cm):</td>
                                            <td className="p-1">
                                                <Number
                                                    value={productSources?.limeGypsumDepthCm ?? 30}
                                                    change={v => productSources && setProductSources({...productSources, limeGypsumDepthCm: v})}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('pk_depth')} (cm):</td>
                                            <td className="p-1">
                                                <Number
                                                    value={productSources?.pkDepthCm ?? 30}
                                                    change={v => productSources && setProductSources({...productSources, pkDepthCm: v})}
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div className="flex text-sm">
                                    <div className="flex-1">
                                        <div className="font-bold text-primary-800 tracking-wider pl-2">{context.word('status')}</div>
                                        <div className="pl-2">
                                            <img
                                                className="text-red-800 stoke-current px-1 w-6 h-6 inline-block align-middle"
                                                src="/icons/irrigation.svg" alt="created"/>
                                            <span className="align-middle">{context.word('created')}: {dateFormat(program.created)}</span>
                                        </div>
                                        <div className="pl-2">
                                            <img
                                                className="text-red-800 stoke-current px-1 w-6 h-6 inline-block align-middle"
                                                src="/icons/aim.svg" alt="updated"/>
                                            <span className="align-middle">{context.word('updated')}: {dateFormat(program.updated)}</span>
                                        </div>
                                        {isSubmitted && (
                                            <div className="pl-2">
                                                <img
                                                    className="text-red-800 stoke-current px-1 w-6 h-6 inline-block align-middle"
                                                    src="/icons/trees.svg" alt="submitted"/>
                                                <span className="align-middle">{context.word('submitted')}: {dateFormat(program.submittedOn)}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex-1 text-sm">
                                        <div className="font-bold text-primary-800 tracking-wider pl-2">{context.word('approval')}</div>
                                        {isApproved ? (
                                            <>
                                                <div className="pl-2">
                                                    <img
                                                        className="text-red-800 stoke-current px-1 w-6 h-6 inline-block align-middle"
                                                        src="/icons/irrigation.svg" alt="approved"/>
                                                    <span className="align-middle">{program.approvedBy}{program.autoApproved ? ` (${context.word('auto_approved')})` : ''}</span>
                                                </div>
                                                <div className="pl-2">
                                                    <img
                                                        className="text-red-800 stoke-current px-1 w-6 h-6 inline-block align-middle"
                                                        src="/icons/aim.svg" alt="approved date"/>
                                                    <span className="align-middle">{dateFormat(program.approvedOn)}</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="pl-2">{context.word('not_yet_approved')}</div>
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                ))
                .case('blocks', () => (
                    <div className="flex-1 overflow-auto">
                        <LimeBlocksTab
                            sampleCounts={sampleCounts}
                            unmatchedCount={unmatchedCount}
                            selectedLandIds={selectedLandIds}
                            onSelectedChange={ids => {
                                setSelectedLandIds(ids)
                                setLabInputsChanged(true)
                            }}
                            farmLands={farmLands}
                            geometryMap={geometryMap}
                            showUnmatched={showUnmatched}
                            onShowUnmatchedChange={value => {
                                setShowUnmatched(value)
                                setLabInputsChanged(true)
                            }}
                        />
                    </div>
                ))
                .case('playfield', () => {

                    const productNames = getProductNames(products, productSources, context);

                    return (
                        <div className="p-4 flex-1 min-h-0 flex flex-col overflow-hidden">
                            <LimePlayfieldUnified
                                data={filteredLabInputs}
                                setData={updateLabInputs}
                                calculatedRows={filteredCalculatedRows}
                                updateRowOverride={updateRowOverride}
                                updateMultipleRowOverrides={updateMultipleRowOverrides}
                                labInputUnit={labInputUnit}
                                landIds={selectedLandIds}
                                products={productNames}
                                totalRows={labInputs.rows.length}
                                productSelected={{
                                    calcitic: productSources?.calciticLimeProductId != null,
                                    dolomitic: productSources?.dolomiticLimeProductId != null,
                                    magnesium: productSources?.magnesiumProductId != null,
                                    gypsum: productSources?.gypsumProductId != null,
                                    potassium: productSources?.potassiumProductId != null,
                                    phosphate: productSources?.phosphateProductId != null,
                                }}
                            />
                        </div>
                    )
                })
                .case('output', () => {
                    const productNames = getProductNames(products, productSources, context);
                    return (
                        <LimeOutputTab
                            labInputs={filteredLabInputs}
                            calculatedRows={filteredCalculatedRows}
                            products={productNames}
                        />
                    )
                })
                .default(() => null)
            )}

            {/* Sample Locations Dialog (blocks tab) */}
            <SampleLocationsDialog
                show={showSampleLocations}
                setShow={setShowSampleLocations}
                rows={sampleRows}
                samplePoints={samplePoints}
                multipleBoundaries={multipleBoundaries}
                boundaryLoading={boundaryLoading}
                boundaryError={boundaryError}
                meq100gMode={meq100gMode}
            />

            {/* Application Data Dialog — exclude unmatched samples from interpolation */}
            <ApplicationDataDialog
                show={showApplicationData}
                setShow={setShowApplicationData}
                labInputs={{rows: filteredLabInputs.rows.filter(r => r.landId !== null)}}
                calculatedRows={filteredCalculatedRows.filter((_, i) => filteredLabInputs.rows[i]?.landId !== null)}
                multipleBoundaries={multipleBoundaries}
                boundaryLoading={boundaryLoading}
                boundaryError={boundaryError}
                programId={id}
                labInputUnit={labInputUnit}
            />

            {/* Footer Actions */}
            <div className="text-right pr-8">
                {tab === 'blocks' && samplePoints.length > 0 && (
                    <div
                        className="btn bg-secondary m-2"
                        onClick={() => setShowSampleLocations(true)}
                    >
                        {context.word('sample_locations')}
                    </div>
                )}

                {tab === 'playfield' && calculatedRows.length > 0 && (
                    <div
                        className={`btn bg-secondary m-2 ${somethingChanged ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => !somethingChanged && setShowApplicationData(true)}
                        title={somethingChanged ? context.word('save_before_interpolation') : ''}
                    >
                        {context.word('application_data')}
                    </div>
                )}

                {somethingChanged && (
                    <div
                        className="btn bg-primary m-2"
                        onClick={() => {
                            if (isSubmitted || isApproved) {
                                setWarningPopup(true)
                            } else {
                                saveProgram()
                            }
                        }}
                    >
                        {context.word('save')}
                    </div>
                )}

                {!props.footer && !isSubmitted && !isApproved && canAutoApprove && (
                    <div
                        className="btn bg-primary m-2"
                        onClick={finishProgram}
                    >
                        {context.word('finish')}
                    </div>
                )}

                {!props.footer && !isSubmitted && !isApproved && !canAutoApprove && (
                    <div
                        className="btn bg-primary m-2"
                        onClick={() => setShowApprover(true)}
                    >
                        {context.word('submit_approval')}
                    </div>
                )}

                {!props.footer && isSubmitted && !isApproved && (
                    <div className="btn bg-gray-500 m-2">
                        {context.word('waiting_approval')}
                    </div>
                )}

                {props.footer}
            </div>
        </div>
    )
}

export default LimeProgram
